import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { InlineError } from './InlineError';
import { columnLabel, formatFigure } from '../lib/model';
import type { FigureComponent, FigureWorkings, ModelCell } from '../lib/types';

// How a figure was arrived at. A booked line shows the transactions posted to
// its accounts; a computed one shows the lines it was worked out from — gross
// profit has no accounts of its own and never will, so asking after them would
// be the wrong question.
//
// Shared by the Model sheet and a business area's Summary, so "Underlying data"
// on a metric opens the same working the sheet does. A component opens its own
// working in place: the same period, the line it names.
export default function WorkingsModal({ cell: start, unit: startUnit, organization: startOrganization, onClose }: {
    cell: ModelCell;
    unit: string;
    organization: string;
    onClose: () => void;
}) {
    const { authFetch } = useAuth();
    const [{ cell, unit, organization }, setCurrent] = useState(
        { cell: start, unit: startUnit, organization: startOrganization });
    useEffect(() => setCurrent({ cell: start, unit: startUnit, organization: startOrganization }),
        [start, startUnit, startOrganization]);
    // A group figure's part opens that company's own working.
    const onTrace = (part: FigureComponent) => setCurrent({
        cell: { ...cell, key: '', concept: part.concept, value: part.value, kind: part.kind ?? 'computed' },
        unit: part.unit ?? unit,
        organization: part.organization ?? organization,
    });
    const [data, setData] = useState<FigureWorkings | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const params = new URLSearchParams({
            organization,
            concept: cell.concept,
            basis: cell.basis,
            grain: cell.period.grain,
            start: cell.period.start ?? cell.period.end,
            end: cell.period.end,
        });
        setData(null);
        authFetch(`/knowledge/model/workings?${params.toString()}`)
            .then(r => r.ok ? r.json() : Promise.reject(new Error('Could not load the working')))
            .then((d: FigureWorkings) => setData(d))
            .catch(err => setError(err.message));
    }, [authFetch, cell.basis, cell.concept, cell.period.end, cell.period.grain,
        cell.period.start, organization]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    const figure = cell.value.amount;
    const booked = data?.total;
    const agrees = figure !== undefined && booked !== undefined
        && Math.abs(figure - booked) < 0.01;

    return (
        <div
            className="fixed inset-0 z-50 flex items-start justify-center p-4 sm:p-8 bg-graphite/30"
            onClick={onClose}
        >
            <div
                className="bg-white rounded-xl border border-softgrey shadow-xl w-full max-w-3xl max-h-[85vh] overflow-y-auto"
                onClick={e => e.stopPropagation()}
                role="dialog"
                aria-label="The transactions behind this figure"
            >
                <div className="px-5 py-4 border-b border-softgrey flex items-start justify-between gap-3 sticky top-0 bg-white">
                    <div>
                        <h4 className="navaris-h3">
                            Behind {formatFigure(cell, unit)}
                        </h4>
                        <p className="text-xs text-coolslate">
                            {columnLabel(cell.period)} · {cell.concept}
                        </p>
                    </div>
                    <button onClick={onClose} aria-label="Close" className="p-1 -m-1 text-coolslate hover:text-graphite">
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <div className="p-5">
                    {error && <InlineError message={error} />}
                    {!data && !error && (
                        <div className="flex justify-center py-10">
                            <Loader2 className="w-5 h-5 animate-spin text-brand" />
                        </div>
                    )}

                    {data && data.components.length > 0 && (
                        <>
                            <p className="text-sm text-coolslate mb-3">
                                {data.nature === 'group'
                                    ? `The group's ${data.name.toLowerCase()} adds up its companies' own, less what passed between them.`
                                    : `${data.name} is worked out from these lines, so it has no accounts of its own.`}
                            </p>
                            <div className="border border-softgrey rounded-lg divide-y divide-softgrey">
                                {data.components.map(part => (
                                    <button
                                        key={part.concept}
                                        type="button"
                                        disabled={!part.present}
                                        onClick={() => onTrace(part)}
                                        className="w-full flex items-center justify-between gap-3 px-3 py-2 text-sm text-left hover:bg-offwhite disabled:hover:bg-transparent"
                                    >
                                        <span>
                                            {part.name}
                                            {part.sign === 'expense' && (
                                                <span className="text-xs text-coolslate"> less</span>
                                            )}
                                        </span>
                                        <span className={part.present ? '' : 'text-coolslate'}>
                                            {part.present
                                                ? formatFigure({ value: part.value } as ModelCell, part.unit ?? 'currency')
                                                : 'not recorded'}
                                        </span>
                                    </button>
                                ))}
                            </div>
                            <p className="text-xs text-coolslate mt-3">
                                Each line opens its own working, so a figure can be followed all
                                the way back to the transactions underneath it.
                            </p>
                        </>
                    )}

                    {data && (data.records?.length ?? 0) > 0 && (
                        <div className={data.components.length > 0 ? 'mt-5' : ''}>
                            <p className="text-sm text-coolslate mb-3">
                                {data.name} counts these records.
                            </p>
                            <ul className="border border-softgrey rounded-lg divide-y divide-softgrey max-h-80 overflow-y-auto">
                                {data.records!.map(r => (
                                    <li key={`${r.resource}:${r.key}`} className="flex items-baseline justify-between gap-3 px-3 py-2 text-sm">
                                        <span className="text-graphite">{r.name}</span>
                                        {r.note && <span className="text-xs text-coolslate text-right">{r.note}</span>}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {data && (data.breakdown?.length ?? 0) > 0 && (
                        <div className="mb-5">
                            <p className="text-sm text-coolslate mb-3">{data.name} at this date, by member.</p>
                            <ul className="border border-softgrey rounded-lg divide-y divide-softgrey">
                                {data.breakdown!.map((r, i) => (
                                    <li key={i} className="flex items-baseline justify-between gap-3 px-3 py-2 text-sm">
                                        <span className="text-graphite">{Object.values(r.members).join(', ')}</span>
                                        <span className="text-right">
                                            {formatFigure({ value: r.value } as ModelCell, unit)}
                                            {r.source?.name && <span className="block text-xs text-coolslate">{r.source.name}</span>}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {data && (data.observations?.length ?? 0) > 1 && (
                        <div className="mb-5">
                            <p className="text-sm text-coolslate mb-3">
                                More than one source gives a figure for this date. The first is the one in force.
                            </p>
                            <ul className="border border-softgrey rounded-lg divide-y divide-softgrey">
                                {data.observations!.map((o, i) => (
                                    <li key={i} className="flex items-baseline justify-between gap-3 px-3 py-2 text-sm">
                                        <span className="text-graphite">{o.source?.name ?? o.kind}</span>
                                        <span>{formatFigure({ value: o.value } as ModelCell, unit)}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {data && data.components.length === 0 && data.transactions.length === 0
                        && (data.records?.length ?? 0) === 0 && (data.breakdown?.length ?? 0) === 0
                        && (data.observations?.length ?? 0) <= 1 && (
                        <p className="text-sm text-coolslate">
                            {data.accounts.length > 0 ? (
                                <>Nothing was posted to {data.accounts.join(', ')} in this period,
                                so this figure came from the statement your accounting system
                                printed rather than from transactions.</>
                            ) : data.nature === 'computed' ? (
                                <>{data.name} is worked out from other lines, and none of them has
                                a figure for this period.</>
                            ) : (
                                <>No account is on this line of the model yet, so there is nothing
                                to trace this figure to. Place the accounts that belong here on the
                                Accounts tab and it will fill in.</>
                            )}
                        </p>
                    )}

                    {data && data.transactions.length > 0 && (
                        <>
                            <div className="overflow-x-auto border border-softgrey rounded-lg">
                                <table className="min-w-max w-full text-sm">
                                    <thead>
                                        <tr className="bg-offwhite text-left">
                                            <th className="font-medium px-3 py-2">Date</th>
                                            <th className="font-medium px-3 py-2">Document</th>
                                            <th className="font-medium px-3 py-2">Who</th>
                                            <th className="font-medium px-3 py-2">Description</th>
                                            <th className="font-medium px-3 py-2">Account</th>
                                            <th className="font-medium px-3 py-2 text-right">Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.transactions.map(row => (
                                            <tr key={row.key} className="border-t border-softgrey">
                                                <td className="px-3 py-2 whitespace-nowrap">{row.on}</td>
                                                <td className="px-3 py-2 whitespace-nowrap">
                                                    {row.document.key ? (
                                                        <Link
                                                            to={`/knowledge`}
                                                            className="text-brand hover:text-brand-dark"
                                                            title={row.document.key}
                                                        >
                                                            {row.document.number ?? row.document.resource}
                                                        </Link>
                                                    ) : '—'}
                                                    {row.document.resource === 'credit_note' && (
                                                        <span className="text-xs text-coolslate"> credit</span>
                                                    )}
                                                </td>
                                                <td className="px-3 py-2 whitespace-nowrap">{row.counterparty ?? '—'}</td>
                                                <td className="px-3 py-2">{row.description}</td>
                                                <td className="px-3 py-2 whitespace-nowrap text-coolslate">
                                                    {row.account.name ?? row.account.key}
                                                </td>
                                                <td className="px-3 py-2 text-right whitespace-nowrap">
                                                    {formatFigure(
                                                        { value: row.amount } as ModelCell, unit,
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr className="border-t border-softgrey bg-offwhite">
                                            <td className="px-3 py-2 font-medium" colSpan={5}>
                                                {data.transactions.length} line
                                                {data.transactions.length === 1 ? '' : 's'}, net of tax
                                            </td>
                                            <td className="px-3 py-2 text-right font-medium">
                                                {formatFigure(
                                                    { value: { amount: data.total, unit: cell.value.unit } } as ModelCell,
                                                    unit,
                                                )}
                                            </td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                            <p className="text-xs text-coolslate mt-3">
                                {agrees
                                    ? 'These lines add up to the figure.'
                                    : 'These lines do not add up to the figure — the rest came from '
                                      + 'entries made in the accounting system itself, or from an '
                                      + 'account not yet on this line.'}
                            </p>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
